import { type HttpResponse } from '@wix/yoshi-flow-editor';
import { resolveId } from '@wix/communities-blog-client-common';
import { resetPostMetadataLaoded } from '@app/external/common/actions/fetch-post-metadata';
import { getIsPostMetadataLoaded } from '@app/external/common/store/is-loaded/is-loaded-selectors';
import { completeFetchPost, fetchPost, preFetchPost } from '../../../common/actions/fetch-post';
import type { PlatformApi } from '../../../common/controller/platform-api';
import { resolvePostSlug } from '../../../common/services/slug';
import {
  type AppStore,
  type ControllerConfig,
  type FlowAPI,
  type NormalizedPost,
  type RouteResolverFn,
} from '../../../common/types';
import { setReadingSessionId } from '../../actions/reading-session-id-actions';
import { ROUTE_404, ROUTE_429 } from '../../constants/routes';
import { postPageRouterPostHandler as handlePostPageRouterPost } from './post-page-router-post-handler';
import { createSetPostIdToSlotsFunction } from './slot-handler';

interface CreatePostPageRouterParams {
  store: AppStore;
  platformApi: PlatformApi;
  flowAPI: FlowAPI;
  slotAPIFactory: Record<string, any>;
  controllerConfig: ControllerConfig;
}

export const createPostPageRouter =
  ({
    store,
    platformApi,
    flowAPI,
    slotAPIFactory,
    controllerConfig,
  }: CreatePostPageRouterParams): RouteResolverFn =>
  async ({ params }, redirect, { preFetch, preFetchResult }) => {
    const postSlug = resolvePostSlug(params);

    if (!postSlug) {
      throw new Error('Post slug is not available');
    }

    const warmupData = platformApi.window.warmupData;
    const isSSR = platformApi.window.rendering.env === 'backend';

    const setPostIdToSlots = createSetPostIdToSlotsFunction(slotAPIFactory);

    /*
      The slots only need the post id, and `fetchPost` below already resolves the post by slug
      and hands back its id. Asking `getPostBySlug` for the same id first only bought us a
      second, serial round trip to the blog API on the SSR critical path, so the id is now read
      off the fetched post instead. On the client the warmed up id is still free to read, and it
      lets the slots be populated without waiting for the post at all.
    */
    const warmedUpPostId = !isSSR ? warmupData.get(postSlug) : undefined;

    if (warmedUpPostId) {
      setPostIdToSlots(warmedUpPostId);
    }

    const state = store.getState();
    const isMetadataLoaded = getIsPostMetadataLoaded(state);

    if (isMetadataLoaded) {
      store.dispatch(resetPostMetadataLaoded());
    }

    if (preFetch) {
      return store.dispatch(preFetchPost(postSlug));
    }

    const readingSessionId = `${parseInt(`${Math.random() * 10000000}`, 10)}-${Date.now()}`;
    store.dispatch(setReadingSessionId(readingSessionId));

    return (
      preFetchResult
        ? store.dispatch(completeFetchPost(postSlug, preFetchResult))
        : store.dispatch(fetchPost(postSlug))
    )
      .then((post: NormalizedPost) => {
        if (!warmedUpPostId) {
          const postId = resolveId(post) ?? undefined;

          setPostIdToSlots(postId);

          if (isSSR && typeof postSlug === 'string' && typeof postId === 'string') {
            warmupData.set(postSlug, postId);
          }
        }

        return handlePostPageRouterPost({
          post,
          store,
          platformApi,
          flowAPI,
          controllerConfig,
        });
      })
      .catch((error: HttpResponse) => {
        if (error.status === 401) {
          return redirect(`/login?redirect=/${postSlug}`);
        }

        if (error.status === 404) {
          return redirect(ROUTE_404);
        }

        if (error.status === 429) {
          return redirect(ROUTE_429);
        }

        throw error;
      });
  };
